import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { ILiveScore, IThirdPartyLiveScore } from '@/interface/ILiveScore';
import type { IMatch, ITournamentCenterEntranceMatch } from '@/interface/IMatch';
import { LiveScoreProvider, MarketPage, MatchStatus, SportType } from '@/interface/enum';
import { computedInterval } from '@/libraries/composables/computedInterval';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { getLiveDisplayTime } from '@/libraries/helpers/matchInfoHelper';
import { formatLiveScore, getLiveScoreDisplay } from '@/libraries/helpers/scoreHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';
import { useLiveScoreQuery, useLiveScoreSubscription } from '@/types';

type DisplayLiveScoreDataType = {
    scoreProvider: LiveScoreProvider;
    detailProvider: LiveScoreProvider;
    liveScoreInfoDetail: string;
    homeScore: string | number;
    awayScore: string | number;
}

const displayScoreHandlers: Record<LiveScoreProvider, (matchInfo: IMatch | ITournamentCenterEntranceMatch, liveScoreInfo: ILiveScore) => DisplayLiveScoreDataType | null> = {
    [LiveScoreProvider.None]: matchInfo => getExtraInfoDisplayLiveScore(matchInfo),
    [LiveScoreProvider.Sbo]: matchInfo => getExtraInfoDisplayLiveScore(matchInfo),
    [LiveScoreProvider.Sdt]: getSdtDisplayLiveScore,
    [LiveScoreProvider.BetRadar]: getBetRadarDisplayLiveScore,
    [LiveScoreProvider.Cricket]: (_, liveScoreInfo) => getCricketDisplayLiveScore(liveScoreInfo),
    [LiveScoreProvider.BetConstruct]: getBetConstructLiveScoreDisplay,
    [LiveScoreProvider.BetGenius]: getBetGeniusLiveScoreDisplay,
};

export default function useMatchInfoScore(matchInfo: Ref<IMatch | ITournamentCenterEntranceMatch>, isEnabled: Ref<boolean> = ref(true)) {
    const liveScoreInfo = ref<ILiveScore>({
        sdt: null,
        betRadar: null,
        cricket: null,
        betConstruct: null,
        betGenius: null,
    });

    const { isWsConnected } = storeToRefs(useRootStore());
    const { liveScoreProviderConfig, isUsSportsLiveScoreEnabled } = storeToRefs(useFeatureToggleStore());

    const supportProvider = computed(() => ((liveScoreProviderConfig.value) ? liveScoreProviderConfig.value[matchInfo.value?.sportType] ?? [] : []));
    const isInLiveMarket = computed(() => matchInfo.value?.marketPage === MarketPage.Live);
    const isSupportBetRadar = computed(() => isUsSportsLiveScoreEnabled.value && supportProvider.value.includes(LiveScoreProvider.BetRadar));
    const isSupportCricket = computed(() => supportProvider.value.includes(LiveScoreProvider.Cricket));
    const isSupportSdt = computed(() => supportProvider.value.includes(LiveScoreProvider.Sdt));
    const isSupportBetConstruct = computed(() => supportProvider.value.includes(LiveScoreProvider.BetConstruct));
    const isSupportBetGenius = computed(() => supportProvider.value.includes(LiveScoreProvider.BetGenius));

    // don't use compute!!! subscription will be broken
    const is3rdPartyLiveScore = ref(false);
    watch([isInLiveMarket, isSupportSdt, isSupportCricket, isSupportBetRadar, isSupportBetConstruct, isSupportBetGenius], () => {
        is3rdPartyLiveScore.value = isInLiveMarket.value && (isSupportSdt.value || isSupportCricket.value || isSupportBetRadar.value || isSupportBetConstruct.value || isSupportBetGenius.value);
    }, { immediate: true });

    // query
    const variables = deepCompareComputed(() => ({
        query: { id: matchInfo.value?.eventId },
    }));

    const getPollInterval = () => {
        if (isWsConnected.value) {
            return undefined;
        }
        return 30000;
    };

    const { onResult: onQueryResult } = useLiveScoreQuery(
        variables,
        deepCompareComputed(() => ({
            pollInterval: getPollInterval(),
            enabled: is3rdPartyLiveScore.value && isEnabled.value,
        })),
    );

    // subscription
    const { onSubscription } = useLiveScoreSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isWsConnected.value && is3rdPartyLiveScore.value && isEnabled.value,
        })),
    );

    onQueryResult((result) => {
        liveScoreInfo.value = formatLiveScore(result);
    });
    onSubscription((result) => {
        liveScoreInfo.value = formatLiveScore(result);
    });

    const sboScore = computedInterval(() => getExtraInfoDisplayLiveScore(matchInfo.value), 20_000);
    const displayLiveScoreData = computed(() => {
        if (isInLiveMarket.value && supportProvider.value.length > 0) {
            const providerResults = supportProvider.value
                .map(provider => provider as LiveScoreProvider)
                .map((provider) => {
                    if (provider === LiveScoreProvider.Sbo) {
                        return sboScore.value;
                    }
                    const handler = displayScoreHandlers[provider];
                    return handler ? handler(matchInfo.value, liveScoreInfo.value) : null;
                });

            const scoreSource = providerResults.find(data => data !== null && data.homeScore !== '' && data.awayScore !== '');
            const detailSource = providerResults.find(data => data?.liveScoreInfoDetail);

            if (scoreSource) {
                return {
                    ...scoreSource,
                    liveScoreInfoDetail: detailSource?.liveScoreInfoDetail ?? scoreSource.liveScoreInfoDetail,
                    detailProvider: detailSource?.detailProvider ?? scoreSource.detailProvider,
                };
            }
        }
        return sboScore.value;
    });

    return {
        displayLiveScoreData,
        liveScoreInfo,
    };
}

const ncaaPeriodDisplayMapping: Record<number, string> = {
    1: '1H',
    2: '1H',
    3: '2H',
    4: '2H',
    5: '2H',
    6: '2H',
    7: '2H',
    8: '2H',
    9: '2H',
    10: i18n.t('matchInfo_scoreDisplay_OT'),
    11: i18n.t('matchInfo_scoreDisplay_OT'),
};
const basketballPeriodDisplayMapping: Record<number, string> = {
    1: i18n.t('matchInfo_scoreDisplay_quarter', { n: 1 }),
    2: i18n.t('matchInfo_scoreDisplay_quarter', { n: 1 }),
    3: i18n.t('matchInfo_scoreDisplay_quarter', { n: 2 }),
    4: i18n.t('matchInfo_scoreDisplay_quarter', { n: 2 }),
    5: i18n.t('matchInfo_scoreDisplay_quarter', { n: 3 }),
    6: i18n.t('matchInfo_scoreDisplay_quarter', { n: 3 }),
    7: i18n.t('matchInfo_scoreDisplay_quarter', { n: 4 }),
    8: i18n.t('matchInfo_scoreDisplay_quarter', { n: 4 }),
    9: i18n.t('matchInfo_scoreDisplay_quarter', { n: 4 }),
    10: '--',
    11: i18n.t('matchInfo_scoreDisplay_OT'),
    12: i18n.t('matchInfo_scoreDisplay_OT'),
    13: '--',
    14: '--',
    15: '--',
    16: '--',
    17: '--',
};

export function getSdtPeriodDisplay(leagueName: string, matchInfo: IMatch | ITournamentCenterEntranceMatch, status: number): string {
    if (!status) {
        return '';
    }

    if (matchInfo.sportType === SportType.Basketball && isNcaaLeague(leagueName)) {
        return ncaaPeriodDisplayMapping[status];
    }

    return basketballPeriodDisplayMapping[status];
}

function getSdtDisplayLiveScore(matchInfo: IMatch | ITournamentCenterEntranceMatch, liveScoreInfo: ILiveScore): DisplayLiveScoreDataType | null {
    if (!liveScoreInfo?.sdt?.status || !matchInfo) {
        return null;
    }

    const leagueName = getTranslated(matchInfo.leagueName);
    const liveScoreInfoDetail = getSdtPeriodDisplay(leagueName, matchInfo, liveScoreInfo.sdt.status);
    return {
        scoreProvider: LiveScoreProvider.Sdt,
        detailProvider: LiveScoreProvider.Sdt,
        liveScoreInfoDetail,
        homeScore: liveScoreInfo.sdt.homeScore,
        awayScore: liveScoreInfo.sdt.awayScore,
    };
}

function getCricketDisplayLiveScore(liveScoreInfo: ILiveScore): DisplayLiveScoreDataType | null {
    if (!liveScoreInfo?.cricket) {
        return null;
    }

    const cricketInfo = liveScoreInfo.cricket;

    const homeScore = cricketInfo
        ? `${cricketInfo.homeScore}/${cricketInfo.homeDismissals}`
        : '0/0';

    const awayScore = cricketInfo
        ? `${cricketInfo.awayScore}/${cricketInfo.awayDismissals}`
        : '0/0';

    const liveScoreInfoDetail = cricketInfo
        ? `INN:${cricketInfo.innings} Over:${Math.max(cricketInfo.over - 1, 0)}.${cricketInfo.delivery}`
        : 'INN:0 Over:0.0';

    return {
        scoreProvider: LiveScoreProvider.Cricket,
        detailProvider: LiveScoreProvider.Cricket,
        liveScoreInfoDetail,
        homeScore,
        awayScore,
    };
}

function getBetRadarDisplayLiveScore(matchInfo: IMatch | ITournamentCenterEntranceMatch, liveScoreInfo: ILiveScore): DisplayLiveScoreDataType | null {
    const { betRadar } = liveScoreInfo;
    if (!betRadar || !matchInfo) {
        return null;
    }

    if (betRadar.matchStatus === MatchStatus.NotStarted) {
        return null;
    }

    const {
        periodSportList,
        quarterSportList,
        inningSportList,
        setGameScoreSportList,
    } = storeToRefs(useFeatureToggleStore());

    if (quarterSportList.value.includes(matchInfo.sportType)) {
        if (isInvalid(betRadar.inPlayPeriod)) {
            return null;
        }

        return {
            scoreProvider: LiveScoreProvider.BetRadar,
            detailProvider: LiveScoreProvider.BetRadar,
            liveScoreInfoDetail: getInPlayQuarterLiveScoreInfoDetail(matchInfo, betRadar.inPlayPeriod),
            homeScore: betRadar.totalHomeScore,
            awayScore: betRadar.totalAwayScore,
        };
    }

    if (setGameScoreSportList.value.includes(matchInfo.sportType)) {
        return {
            scoreProvider: LiveScoreProvider.BetRadar,
            detailProvider: LiveScoreProvider.BetRadar,
            liveScoreInfoDetail: (betRadar.setHomeScore !== null && betRadar.setAwayScore !== null) ? `${i18n.t('matchInfo_scoreDisplay_set')} ${betRadar.setHomeScore} : ${betRadar.setAwayScore}` : '',
            homeScore: betRadar.gameHomeScore,
            awayScore: betRadar.gameAwayScore,
        };
    }

    if (inningSportList.value.includes(matchInfo.sportType)) {
        if (isInvalid(betRadar.inPlayInning)) {
            return null;
        }

        return {
            scoreProvider: LiveScoreProvider.BetRadar,
            detailProvider: LiveScoreProvider.BetRadar,
            liveScoreInfoDetail: getInPlayInningLiveScoreInfoDetail(betRadar.inPlayInning),
            homeScore: betRadar.totalHomeScore,
            awayScore: betRadar.totalAwayScore,
        };
    }
    if (periodSportList.value.includes(matchInfo.sportType)) {
        if (isInvalid(betRadar.inPlayPeriod)) {
            return null;
        }

        return {
            scoreProvider: LiveScoreProvider.BetRadar,
            detailProvider: LiveScoreProvider.BetRadar,
            liveScoreInfoDetail: getInPlayPeriodLiveScoreInfoDetail(betRadar.inPlayPeriod),
            homeScore: betRadar.totalHomeScore,
            awayScore: betRadar.totalAwayScore,
        };
    }

    return null;
}

function getQuarterScoreDisplay(
    matchInfo: IMatch | ITournamentCenterEntranceMatch,
    scoreData: IThirdPartyLiveScore,
    liveScoreProvider: LiveScoreProvider.BetConstruct | LiveScoreProvider.BetGenius,
): DisplayLiveScoreDataType | null {
    if (isInvalid(scoreData.inPlayPeriod)) {
        return null;
    }

    return {
        scoreProvider: liveScoreProvider,
        detailProvider: liveScoreProvider,
        liveScoreInfoDetail: getInPlayQuarterLiveScoreInfoDetail(matchInfo, scoreData.inPlayPeriod),
        homeScore: scoreData.totalHomeScore,
        awayScore: scoreData.totalAwayScore,
    };
}

function getSetGameScoreDisplay(
    scoreData: IThirdPartyLiveScore,
    liveScoreProvider: LiveScoreProvider.BetConstruct | LiveScoreProvider.BetGenius,
): DisplayLiveScoreDataType {
    return {
        scoreProvider: liveScoreProvider,
        detailProvider: liveScoreProvider,
        liveScoreInfoDetail: (scoreData.setHomeScore !== null && scoreData.setAwayScore !== null)
            ? `${i18n.t('matchInfo_scoreDisplay_set')} ${scoreData.setHomeScore} : ${scoreData.setAwayScore}`
            : '',
        homeScore: scoreData.gameHomeScore,
        awayScore: scoreData.gameAwayScore,
    };
}

function getInningScoreDisplay(
    scoreData: IThirdPartyLiveScore,
    liveScoreProvider: LiveScoreProvider.BetConstruct | LiveScoreProvider.BetGenius,
): DisplayLiveScoreDataType | null {
    if (isInvalid(scoreData.inPlayPeriod)) {
        return null;
    }

    return {
        scoreProvider: liveScoreProvider,
        detailProvider: liveScoreProvider,
        liveScoreInfoDetail: getInPlayInningLiveScoreInfoDetail(scoreData.inPlayPeriod),
        homeScore: scoreData.totalHomeScore,
        awayScore: scoreData.totalAwayScore,
    };
}

function getPeriodScoreDisplay(
    scoreData: IThirdPartyLiveScore,
    liveScoreProvider: LiveScoreProvider.BetConstruct | LiveScoreProvider.BetGenius,
): DisplayLiveScoreDataType | null {
    if (isInvalid(scoreData.inPlayPeriod)) {
        return null;
    }

    return {
        scoreProvider: liveScoreProvider,
        detailProvider: liveScoreProvider,
        liveScoreInfoDetail: getInPlayPeriodLiveScoreInfoDetail(scoreData.inPlayPeriod),
        homeScore: scoreData.totalHomeScore,
        awayScore: scoreData.totalAwayScore,
    };
}

function getDefaultThirdPartyScoreDisplay(
    matchInfo: IMatch | ITournamentCenterEntranceMatch,
    scoreData: IThirdPartyLiveScore,
    liveScoreProvider: LiveScoreProvider.BetConstruct | LiveScoreProvider.BetGenius,
): DisplayLiveScoreDataType {
    return {
        scoreProvider: liveScoreProvider,
        detailProvider: liveScoreProvider,
        liveScoreInfoDetail: scoreData.inPlayPeriod === 'PEN'
            ? 'PEN'
            : getExtraLiveScoreInfoDetail(matchInfo),
        homeScore: scoreData.totalHomeScore,
        awayScore: scoreData.totalAwayScore,
    };
}

function getThirdPartyLiveScoreDisplay(
    matchInfo: IMatch | ITournamentCenterEntranceMatch,
    scoreData: IThirdPartyLiveScore,
    liveScoreProvider: LiveScoreProvider.BetConstruct | LiveScoreProvider.BetGenius,
): DisplayLiveScoreDataType | null {
    if (!scoreData || !matchInfo || scoreData.matchStatus === MatchStatus.NotStarted) {
        return null;
    }

    const {
        periodSportList,
        quarterSportList,
        inningSportList,
        setGameScoreSportList,
    } = storeToRefs(useFeatureToggleStore());

    if (quarterSportList.value.includes(matchInfo.sportType)) {
        return getQuarterScoreDisplay(matchInfo, scoreData, liveScoreProvider);
    }

    if (setGameScoreSportList.value.includes(matchInfo.sportType)) {
        return getSetGameScoreDisplay(scoreData, liveScoreProvider);
    }

    if (inningSportList.value.includes(matchInfo.sportType)) {
        return getInningScoreDisplay(scoreData, liveScoreProvider);
    }

    if (periodSportList.value.includes(matchInfo.sportType)) {
        return getPeriodScoreDisplay(scoreData, liveScoreProvider);
    }

    return getDefaultThirdPartyScoreDisplay(matchInfo, scoreData, liveScoreProvider);
}

function getBetConstructLiveScoreDisplay(matchInfo: IMatch | ITournamentCenterEntranceMatch, liveScoreInfo: ILiveScore): DisplayLiveScoreDataType | null {
    if (!liveScoreInfo?.betConstruct) return null;
    return getThirdPartyLiveScoreDisplay(matchInfo, liveScoreInfo.betConstruct, LiveScoreProvider.BetConstruct);
}

function getBetGeniusLiveScoreDisplay(matchInfo: IMatch | ITournamentCenterEntranceMatch, liveScoreInfo: ILiveScore): DisplayLiveScoreDataType | null {
    if (!liveScoreInfo?.betGenius) return null;
    return getThirdPartyLiveScoreDisplay(matchInfo, liveScoreInfo.betGenius, LiveScoreProvider.BetGenius);
}

function getInPlayPeriodLiveScoreInfoDetail(inPlayPeriod: string) {
    if (!inPlayPeriod) {
        return '';
    }
    if (inPlayPeriod === 'OT') {
        return `${i18n.t('matchInfo_scoreDisplay_OT')}`;
    }
    if (inPlayPeriod === 'PEN') {
        return 'PEN';
    }
    return `${i18n.t('matchInfo_scoreDisplay_period')}${inPlayPeriod}`;
}

export function getInPlayQuarterLiveScoreInfoDetail(matchInfo: IMatch | ITournamentCenterEntranceMatch, inPlayInning: string): string {
    if (isInvalid(inPlayInning) || !matchInfo) {
        return '';
    }

    if (inPlayInning === 'OT') {
        return `${i18n.t('matchInfo_scoreDisplay_OT')}`;
    }

    const leagueName = getTranslated(matchInfo.leagueName);
    if (matchInfo.sportType === SportType.Basketball && isNcaaLeague(leagueName)) {
        return inPlayInning === '1' ? i18n.t('matchInfo_scoreDisplay_first_half') : i18n.t('matchInfo_scoreDisplay_second_half');
    }

    return `${i18n.t('matchInfo_scoreDisplay_quarter', {
        n: inPlayInning,
    })}`;
}

function getInPlayInningLiveScoreInfoDetail(inPlayInning: string) {
    if (isInvalid(inPlayInning)) {
        return '';
    }
    if (inPlayInning === 'OT') {
        return `${i18n.t('matchInfo_scoreDisplay_inning')}: ${i18n.t('matchInfo_scoreDisplay_Extra')}`;
    }
    return `${i18n.t('matchInfo_scoreDisplay_inning')}: ${inPlayInning}`;
}

function getExtraInfoDisplayLiveScore(matchInfo: IMatch | ITournamentCenterEntranceMatch): DisplayLiveScoreDataType | null {
    if (!matchInfo) {
        return null;
    }

    return {
        scoreProvider: LiveScoreProvider.Sbo,
        detailProvider: LiveScoreProvider.Sbo,
        liveScoreInfoDetail: getExtraLiveScoreInfoDetail(matchInfo),
        homeScore: getLiveScoreDisplay(matchInfo.mainMarketEventResult.liveHomeScore),
        awayScore: getLiveScoreDisplay(matchInfo.mainMarketEventResult.liveAwayScore),
    };
}

function getExtraLiveScoreInfoDetail(matchInfo: IMatch | ITournamentCenterEntranceMatch) {
    const injuryTime = matchInfo.mainMarketEventResult.extraInfo?.injuryTime ?? 0;
    const injuryTimeText = (injuryTime > 0 && matchInfo.sportType === SportType.Soccer) ? ` (+${injuryTime})` : '';
    const liveScoreInfoDetail = getLiveDisplayTime(matchInfo) + injuryTimeText;
    return liveScoreInfoDetail;
}

function isNcaaLeague(leagueName: string) {
    return leagueName.startsWith('NCAA');
}

function isInvalid(inPlayInfo: string) {
    return !inPlayInfo || inPlayInfo === '0';
}
